/**
 * LuviaRx — public "Issue Refund" page opened from the admin
 * notification email. Token-gated (no login required). Mobile-first.
 * Same UX pattern as /mark-shipped/:token.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { RotateCcw, CheckCircle2, AlertTriangle, DollarSign, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Refund() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [mode, setMode] = useState("full");           // "full" | "partial"
  const [partialAmount, setPartialAmount] = useState("");
  const [reason, setReason] = useState("requested_by_customer");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);          // { refunded_this_time, fully_refunded } | null

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/refund/${token}`)
      .then(({ data }) => { if (!cancelled) setOrder(data); })
      .catch((e) => {
        if (!cancelled) setLoadError(e?.response?.data?.detail || "This link is invalid or expired.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async () => {
    setSubmitError("");
    let amount = null;
    if (mode === "partial") {
      const parsed = parseFloat(partialAmount);
      if (!parsed || parsed <= 0) {
        setSubmitError("Enter a valid partial refund amount.");
        return;
      }
      if (parsed - (order?.refundable_amount || 0) > 0.005) {
        setSubmitError(`Amount can't exceed the refundable balance of $${order.refundable_amount.toFixed(2)}.`);
        return;
      }
      amount = parsed;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/refund/${token}`, { amount, reason });
      setResult(data);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw : "Could not issue refund. Please try again.";
      setSubmitError(msg);
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Shell><div className="text-charcoal-400 text-sm">Loading order…</div></Shell>;
  }
  if (loadError || !order) {
    return (
      <Shell>
        <div data-testid="refund-error" className="text-center py-6">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" strokeWidth={1.8} />
          <p className="font-display text-2xl text-charcoal-600">Link unavailable</p>
          <p className="text-sm text-charcoal-400 mt-2">{loadError || "Order not found."}</p>
        </div>
      </Shell>
    );
  }
  if (result || order.fully_refunded) {
    const info = result || { refunded_this_time: order.refunded_amount, fully_refunded: true, already_refunded: true };
    return (
      <Shell>
        <div data-testid="refund-success" className="text-center py-6">
          <CheckCircle2 className="w-12 h-12 mx-auto text-sage-600 mb-3" strokeWidth={1.8} />
          <p className="overline mb-1">
            {info.fully_refunded ? "Fully refunded" : "Partial refund issued"}
          </p>
          <p className="font-display text-3xl text-charcoal-600 mb-2">
            ${Number(info.refunded_this_time || 0).toFixed(2)}
          </p>
          <p className="text-sm text-charcoal-500 leading-relaxed max-w-xs mx-auto">
            {info.already_refunded
              ? "This order has already been refunded — no further action needed."
              : "The customer has been emailed. Funds land back on their card in 5-10 business days."}
          </p>
          <p className="text-xs text-charcoal-300 mt-4">
            Order <span className="font-medium text-charcoal-500">{order.order_number}</span>
          </p>
        </div>
      </Shell>
    );
  }

  const refundable = order.refundable_amount;
  const alreadyRefunded = order.refunded_amount || 0;

  return (
    <Shell>
      <header className="mb-5">
        <div className="flex items-center gap-2 mb-2">
          <RotateCcw className="w-4 h-4 text-[#8B2252]" strokeWidth={2} />
          <p className="overline">Issue refund</p>
        </div>
        <p className="font-display text-3xl text-charcoal-600 font-bold">{order.order_number}</p>
        <p className="text-xs text-charcoal-400 mt-1">
          <span className="capitalize">{order.payment_method}</span> · Paid ${order.total.toFixed(2)}
          {alreadyRefunded > 0 && (
            <span className="text-amber-700"> · Already refunded ${alreadyRefunded.toFixed(2)}</span>
          )}
        </p>
      </header>

      <section className="bg-cream-200/50 rounded-2xl p-4 mb-4 text-sm">
        <p className="text-[10px] uppercase tracking-[0.16em] text-charcoal-300 font-medium mb-1">Customer</p>
        <p className="font-medium text-charcoal-600">{order.customer_name || "—"}</p>
        <p className="text-charcoal-400 text-xs">{order.customer_email_masked}</p>
      </section>

      <section className="bg-cream-200/50 rounded-2xl p-4 mb-5 text-sm">
        <p className="text-[10px] uppercase tracking-[0.16em] text-charcoal-300 font-medium mb-2">
          Items ({order.items?.length || 0})
        </p>
        <ul className="space-y-1.5">
          {(order.items || []).map((it, idx) => (
            <li key={idx} className="flex justify-between gap-4 text-charcoal-500">
              <span className="truncate">
                <span className="font-medium text-charcoal-600">{it.quantity}×</span>{" "}
                {it.product_name} — {it.variant_label}
              </span>
              <span className="text-charcoal-400 shrink-0">${Number(it.unit_price).toFixed(2)}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className="mb-4 grid gap-3">
        <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-400 font-medium">
          Refund amount · ${refundable.toFixed(2)} refundable
        </p>
        <RefundModeOption
          testid="refund-mode-full"
          selected={mode === "full"}
          onClick={() => setMode("full")}
          title={`Full refund · $${refundable.toFixed(2)}`}
          subtitle="Refunds the entire remaining balance."
        />
        <RefundModeOption
          testid="refund-mode-partial"
          selected={mode === "partial"}
          onClick={() => setMode("partial")}
          title="Partial refund"
          subtitle="Enter a custom amount below."
        />
        {mode === "partial" && (
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-300" strokeWidth={2} />
            <input
              data-testid="refund-partial-input"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0.01"
              max={refundable}
              value={partialAmount}
              onChange={(e) => setPartialAmount(e.target.value)}
              placeholder={`Max ${refundable.toFixed(2)}`}
              className="w-full pl-10 pr-4 py-3 rounded-2xl border-2 border-charcoal-100 bg-cream-100 text-charcoal-600 focus:border-[#8B2252] focus:outline-none"
            />
          </div>
        )}
      </div>

      <div className="mb-5">
        <label
          htmlFor="refund-reason"
          className="block text-[10px] uppercase tracking-[0.18em] text-charcoal-400 font-medium mb-2"
        >
          Reason
        </label>
        <select
          id="refund-reason"
          data-testid="refund-reason-select"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full px-4 py-3 rounded-2xl border-2 border-charcoal-100 bg-cream-100 text-charcoal-600 focus:border-[#8B2252] focus:outline-none"
        >
          <option value="requested_by_customer">Customer request</option>
          <option value="duplicate">Duplicate charge</option>
          <option value="fraudulent">Fraudulent</option>
        </select>
      </div>

      {submitError && (
        <div
          data-testid="refund-form-error"
          className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3"
        >
          {submitError}
        </div>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={submitting}
        data-testid="refund-submit"
        className="w-full inline-flex items-center justify-center gap-2 bg-[#8B2252] text-white px-6 py-4 rounded-full font-display font-semibold text-base hover:bg-[#711b42] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <RotateCcw className="w-4 h-4" strokeWidth={2} />
        {submitting ? "Issuing refund…" : "Issue refund"}
        <ArrowRight className="w-4 h-4" strokeWidth={2} />
      </button>

      <p className="mt-5 text-[11px] text-charcoal-400 text-center leading-relaxed">
        The customer will get an email confirming their refund. Funds appear on their card in 5-10 business days.
      </p>
    </Shell>
  );
}

const RefundModeOption = ({ testid, selected, onClick, title, subtitle }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-left rounded-2xl border-2 p-4 transition-all duration-200 flex items-start gap-3 ${
      selected
        ? "border-[#8B2252] bg-[#8B2252]/[0.04]"
        : "border-charcoal-100 bg-cream-100 hover:border-charcoal-200"
    }`}
  >
    <span
      className={`mt-0.5 w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center ${
        selected ? "border-[#8B2252] bg-[#8B2252]" : "border-charcoal-200 bg-white"
      }`}
    >
      {selected && <span className="w-2 h-2 rounded-full bg-white" />}
    </span>
    <span className="flex-1 min-w-0">
      <span className="block font-display font-bold text-charcoal-600">{title}</span>
      <span className="block text-xs text-charcoal-400 mt-0.5">{subtitle}</span>
    </span>
  </button>
);

const Shell = ({ children }) => (
  <main data-testid="refund-page" className="min-h-[calc(100vh-4rem)] bg-cream-200/40 flex items-start justify-center px-4 py-8 md:py-14">
    <div className="w-full max-w-md surface-card p-6 md:p-8">
      {children}
    </div>
  </main>
);
